import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Stack,
  Avatar,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
  SmartToy as SmartToyIcon,
  ArrowForward as ArrowForwardIcon,
  Star as StarIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  AttachMoney as MoneyIcon,
  EmojiEvents as AwardIcon,
} from '@mui/icons-material';
import Logo from '../common/Logo';
import TechFigures from '../common/TechFigures';
import PartnershipForm from '../partnership/PartnershipForm';

const HomePageEnhanced: React.FC = () => {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [partnershipFormOpen, setPartnershipFormOpen] = useState(false);
  const navigate = useNavigate();

  const features = [
    {
      icon: <SmartToyIcon style={{ fontSize: 40 }} />,
      title: 'AI-Powered Analysis',
      description: 'Advanced AI analyzes your content for structure, SEO, and readability',
      color: 'primary',
    },
    {
      icon: <SecurityIcon style={{ fontSize: 40 }} />,
      title: 'Multi-Tier Verification',
      description: 'Comprehensive verification system builds trust with journalists',
      color: 'success',
    },
    {
      icon: <PublicIcon style={{ fontSize: 40 }} />,
      title: 'Global Distribution',
      description: 'Intelligent geographic targeting and channel recommendations',
      color: 'info',
    },
    {
      icon: <MoneyIcon style={{ fontSize: 40 }} />,
      title: 'Performance-Based Pricing',
      description: 'Pay only for successful publications with transparent billing',
      color: 'warning',
    },
    {
      icon: <AnalyticsIcon style={{ fontSize: 40 }} />,
      title: 'Comprehensive Analytics',
      description: 'Track ROI, engagement, and business impact in real-time',
      color: 'secondary',
    },
    {
      icon: <SpeedIcon style={{ fontSize: 40 }} />,
      title: 'Real-Time Tracking',
      description: 'Live status updates and engagement metrics',
      color: 'error',
    },
  ];

  const stats = [
    { label: 'Active Users', value: '10,000+', icon: <PeopleIcon /> },
    { label: 'PRs Distributed', value: '50,000+', icon: <PublicIcon /> },
    { label: 'Publications', value: '1,200+', icon: <TrendingUpIcon /> },
    { label: 'Success Rate', value: '94%', icon: <StarIcon /> },
  ];

  const testimonials = [
    {
      name: 'Amanda Johnson',
      company: 'TechCorp',
      role: 'Marketing Director',
      content: 'Prowess transformed our PR strategy. The AI insights are incredible.',
      rating: 5,
    },
    {
      name: 'Michael Chen',
      company: 'StartupXYZ',
      role: 'CEO',
      content: 'Finally, a platform that delivers results. Our reach increased 300%.',
      rating: 5,
    },
    {
      name: 'Emily Rodriguez',
      company: 'InnovateLab',
      role: 'PR Manager',
      content: 'The verification system gives us credibility with top-tier publications.',
      rating: 5,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Hero Section */}
      <div
        style={{
          background: '#0927eb',
          color: 'white',
          paddingTop: '64px',
          paddingBottom: '96px',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '100vh',
        }}
      >
        <TechFigures />
        <Container maxWidth="lg">
          <div style={{ textAlign: 'center', position: 'relative', zIndex: 10 }}>
            <div style={{ marginBottom: "32px" }}>
              <Logo size="homepage" animated={true} />
            </div>
            
            
            
            <Typography
              variant="h1"
              style={{
                fontSize: '4rem',
                fontWeight: 700,
                marginBottom: '16px',
                color: 'white',
              }}
            >
              Get PR on Demand
            </Typography>
            
            <Typography
              variant="h5"
              style={{
                marginBottom: '32px',
                opacity: 0.9,
                maxWidth: 600,
                margin: '0 auto 32px auto',
                fontWeight: 400,
                color: 'white',
              }}
            >
              AI-Powered Press Release Distribution & Analytics Platform
            </Typography>
            
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              style={{ marginBottom: '48px' }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                href="/dashboard"
                style={{
                  backgroundColor: '#000000',
                  border: 'none',
                  color: 'white',
                  paddingLeft: '32px',
                  paddingRight: '32px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
                }}
              >
                Get Started Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setPartnershipFormOpen(true)}
                style={{
                  borderColor: '#000000',
                  color: '#000000',
                  borderWidth: '2px',
                  paddingLeft: '32px',
                  paddingRight: '32px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                }}
              >
                Partner with Prowess
              </Button>
            </Stack>

            {/* Stats */}
            <Grid container spacing={3} style={{ marginTop: '48px' }}>
              {stats.map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <Paper
                    style={{
                      padding: '16px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      textAlign: 'center',
                      transition: 'all 0.3s ease-in-out',
                      borderRadius: '16px',
                    }}
                  >
                    <div style={{ color: 'white', marginBottom: '8px' }}>
                      {stat.icon}
                    </div>
                    <Typography variant="h4" style={{ color: 'white', fontWeight: 700 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" style={{ color: 'white', opacity: 0.8 }}>
                      {stat.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </div>
        </Container>
      </div>

      {/* Testimonials Section */}
      <div style={{ backgroundColor: '#f5f5f5', paddingTop: '64px', paddingBottom: '64px' }}>
        <Container maxWidth="lg">
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <Typography variant="h2" style={{ marginBottom: '16px', fontWeight: 700 }}>
              What Our Users Say
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Trusted by thousands of companies worldwide
            </Typography>
          </div>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card style={{ height: '100%' }}>
                  <CardContent style={{ padding: "24px" }}>
                    <div style={{ display: 'flex', marginBottom: '16px' }}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <StarIcon key={i} style={{ color: '#ed6c02', fontSize: 20 }} />
                      ))}
                    </div>
                    <Typography variant="body1" style={{ marginBottom: '24px', fontStyle: 'italic' }}>
                      "{testimonial.content}"
                    </Typography>
                    <div>
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}, {testimonial.company}
                      </Typography>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </div>

      {/* CTA Section */}
      <Container maxWidth="lg" style={{ padding: '64px 0' }}>
        <Paper
          style={{
            padding: '48px',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #0927eb 0%, #3A4FED 100%)',
            color: 'white',
            boxShadow: '0 20px 40px rgba(9, 39, 235, 0.3)',
          }}
        >
          <Typography variant="h3" style={{ marginBottom: '16px', fontWeight: 700 }}>
            Ready to Transform Your PR Strategy?
          </Typography>
          <Typography variant="h6" style={{ marginBottom: '32px', opacity: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            Join thousands of companies using <Logo size="inline" animated={false} /> to amplify their reach
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/auth')}
              style={{
                backgroundColor: '#000000',
                color: 'white',
                padding: '12px 32px',
                fontWeight: 'bold',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/auth')}
              style={{
                borderColor: '#000000',
                color: '#000000',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderWidth: '2px',
                padding: '12px 32px',
                fontWeight: 'bold',
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Paper>
      </Container>

      {/* Partnership Form Dialog */}
      <PartnershipForm 
        open={partnershipFormOpen} 
        onClose={() => setPartnershipFormOpen(false)} 
      />
    </div>
  );
};

export default HomePageEnhanced;
